/**
 * BinanceStreamer — Exness-style tick-by-tick chart updates.
 *
 * Wraps the UDF datafeed shipped by TradingView. For BTC/ETH we stream
 * from Coinbase Exchange's `matches` channel (broker-quality tape — same
 * source as the /history endpoint for these two symbols); for XAU/XAG
 * we stream from Binance USDT-M futures. Every other symbol falls
 * through to the base datafeed's 1s polling.
 *
 * Coinbase docs: https://docs.cdp.coinbase.com/exchange/docs/websocket-channels#matches-channel
 * Binance docs:  https://github.com/binance/binance-spot-api-docs/blob/master/web-socket-streams.md
 */

// Tradelog internal ticker → { source, url, subscribe payload builder, product/symbol id }.
// `source` selects the message-parser branch inside `ws.onmessage`.
//
// NOTE (Feb 2026): Binance USDT-M Futures shipped commodity perps
// (XAUUSDT / XAGUSDT / CLUSDT — Gold, Silver, WTI Crude). Free public
// WS `fstream.binance.com`, no auth, 24/7 tape. Wired them here so those
// three symbols get tick-by-tick candle updates on the native Tradelog
// chart — no TV iframe fallback needed.
const STREAM_MAP = {
  BTCUSD: {
    source: "coinbase",
    url: "wss://ws-feed.exchange.coinbase.com",
    productId: "BTC-USD",
  },
  ETHUSD: {
    source: "coinbase",
    url: "wss://ws-feed.exchange.coinbase.com",
    productId: "ETH-USD",
  },
  XAUUSD: {
    source: "binance-futures",
    url: "wss://fstream.binance.com/ws/xauusdt@trade",
  },
  XAGUSD: {
    source: "binance-futures",
    url: "wss://fstream.binance.com/ws/xagusdt@trade",
  },
  USOIL: {
    source: "binance-futures",
    url: "wss://fstream.binance.com/ws/clusdt@trade",
  },
};

// TradingView resolution (in minutes) → seconds per bucket.
function bucketSecondsFor(resolution) {
  const r = String(resolution);
  if (r === "D" || r === "1D") return 86400;
  if (r === "W" || r === "1W") return 86400 * 7;
  if (r === "M" || r === "1M") return 86400 * 30;
  return Math.max(60, parseInt(r, 10) * 60);
}

function normalizeTicker(sym) {
  if (!sym) return "";
  return String(sym).split(":").pop().toUpperCase();
}

export function hasBinanceStream(symbol) {
  return Boolean(STREAM_MAP[normalizeTicker(symbol)]);
}

/* -------------------------------------------------------------------- *
 * Shared live-price pool. One WebSocket per symbol, many subscribers.
 * Any UI element (chart, OrderPanel, ticker, pnl badge) that calls
 * `subscribeLivePrice(sym, cb)` receives the same tick stream that
 * feeds the chart — so the order-panel price can NEVER drift behind
 * the candle-close price the user is looking at.
 * -------------------------------------------------------------------- */
const _pricePool = new Map(); // ticker → { ws, subs: Set<cb>, lastPrice, closed }

function _openPricePool(ticker) {
  const cfg = STREAM_MAP[ticker];
  if (!cfg) return null;
  const entry = { ws: null, subs: new Set(), lastPrice: null, closed: false };
  _pricePool.set(ticker, entry);

  const connect = () => {
    if (entry.closed) return;
    const ws = new WebSocket(cfg.url);
    entry.ws = ws;
    ws.onopen = () => {
      if (cfg.source === "coinbase") {
        try {
          ws.send(JSON.stringify({
            type: "subscribe",
            product_ids: [cfg.productId],
            channels: ["matches"],
          }));
        } catch (_) { /* ignore */ }
      }
    };
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        let price;
        if (cfg.source === "coinbase") {
          if (msg.type !== "match" && msg.type !== "last_match") return;
          price = parseFloat(msg.price);
        } else if (cfg.source === "binance-futures") {
          // Binance Futures `<sym>@trade` payload: {e:"trade", p:"4370.43", ...}
          // — connects with no subscribe frame, streams every executed
          // trade at up to 100 ms cadence.
          if (msg.e !== "trade") return;
          price = parseFloat(msg.p);
        } else {
          price = parseFloat(msg.p);
        }
        if (!Number.isFinite(price)) return;
        entry.lastPrice = price;
        entry.subs.forEach(cb => { try { cb(price); } catch (_) { /* subscriber threw */ } });
      } catch (_) { /* malformed */ }
    };
    ws.onclose = () => { if (!entry.closed) setTimeout(connect, 1500); };
    ws.onerror = () => { try { ws.close(); } catch (_) { /* ignore */ } };
  };
  connect();
  return entry;
}

/**
 * Subscribe to the same live price stream the chart uses. Returns an
 * unsubscribe function. Safe to call for symbols not in STREAM_MAP — it
 * simply returns a no-op unsub so callers don't have to branch.
 */
export function subscribeLivePrice(symbol, cb) {
  const ticker = normalizeTicker(symbol);
  if (!STREAM_MAP[ticker]) return () => {};
  let entry = _pricePool.get(ticker);
  if (!entry) entry = _openPricePool(ticker);
  entry.subs.add(cb);
  // Fire immediately with last known price so the panel doesn't wait a tick.
  if (entry.lastPrice != null) { try { cb(entry.lastPrice); } catch (_) {} }
  return () => {
    entry.subs.delete(cb);
    if (entry.subs.size === 0) {
      entry.closed = true;
      try { entry.ws && entry.ws.close(); } catch (_) { /* ignore */ }
      _pricePool.delete(ticker);
    }
  };
}

/**
 * Wrap a TradingView-UDF datafeed with a streaming subscribeBars override.
 * Everything else (config, symbols, history, unsubscribeBars) delegates.
 */
export function wrapDatafeedWithBinanceStream(baseDatafeed) {
  const subscribers = {}; // listenerGuid → { ws, currentBar, bucketSec, onTick, ticker }

  return new Proxy(baseDatafeed, {
    get(target, prop) {
      if (prop === "subscribeBars") {
        return function subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onResetCacheNeeded) {
          const ticker = normalizeTicker(symbolInfo?.ticker || symbolInfo?.name);
          const streamCfg = STREAM_MAP[ticker];
          if (!streamCfg) {
            // Not a mapped symbol — fall through to the base datafeed's
            // 1s polling.
            return target.subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onResetCacheNeeded);
          }

          const bucketSec = bucketSecondsFor(resolution);
          const sub = {
            ws: null,
            currentBar: null,
            lastClose: null,      // seeds next bar's OPEN for OHLC continuity
            lastPushMs: 0,        // throttle guard
            bucketSec, onTick, ticker, source: streamCfg.source, closed: false,
          };
          subscribers[listenerGuid] = sub;

          // Throttle: max ~5 emits/sec. H/L still captured on every tick.
          const MIN_PUSH_MS = 200;

          const connect = () => {
            if (sub.closed) return;
            const ws = new WebSocket(streamCfg.url);
            sub.ws = ws;

            ws.onopen = () => {
              // Coinbase requires an explicit subscribe frame per its
              // WebSocket spec. Binance's stream URLs already carry the
              // subscription in the path (no frame needed).
              if (streamCfg.source === "coinbase") {
                try {
                  ws.send(JSON.stringify({
                    type: "subscribe",
                    product_ids: [streamCfg.productId],
                    channels: ["matches"],
                  }));
                } catch (_) { /* socket may have flapped */ }
              }
            };

            ws.onmessage = (evt) => {
              try {
                const msg = JSON.parse(evt.data);
                // Parse price + trade time based on source. Both feeds
                // give one print per real trade — no synthetic aggregation.
                let price, tradeMs, volume;
                if (sub.source === "coinbase") {
                  // { type:"match"|"last_match", product_id:"BTC-USD",
                  //   price:"63000.00", size:"0.001",
                  //   time:"2024-01-01T00:00:00.123456Z" }
                  if (msg.type !== "match" && msg.type !== "last_match") return;
                  price = parseFloat(msg.price);
                  volume = parseFloat(msg.size || 0);
                  tradeMs = msg.time ? Date.parse(msg.time) : Date.now();
                } else {
                  // Binance:
                  //   spot: { e:"trade", p:"63591.5", q:"0.001", T:1786595280000 }
                  //   fut : { e:"trade", p:"4402.87", q:"0.01", T:1786595577315 }
                  price = parseFloat(msg.p);
                  volume = parseFloat(msg.q || 0);
                  tradeMs = Number(msg.T || Date.now());
                }
                if (!Number.isFinite(price)) return;

                // Diagnostic: expose per-symbol tick counts on window so
                // production can be verified from DevTools without shell
                // access — `window.__tradelog_ws_ticks.XAUUSD` grows every
                // time a Binance/Coinbase trade lands here.
                if (typeof window !== "undefined") {
                  window.__tradelog_ws_ticks = window.__tradelog_ws_ticks || {};
                  const key = sub.ticker;
                  window.__tradelog_ws_ticks[key] = (window.__tradelog_ws_ticks[key] || 0) + 1;
                }

                // TradingView UDF uses UNIX **seconds** for `Bar.time`.
                const barTimeSec = Math.floor(tradeMs / 1000 / sub.bucketSec) * sub.bucketSec;
                if (!sub.currentBar || sub.currentBar.time !== barTimeSec) {
                  // New bucket → flush old bar, seed new bar's OPEN from
                  // previous bar's CLOSE so the candle series is gap-less.
                  if (sub.currentBar) {
                    sub.lastClose = sub.currentBar.close;
                    sub.onTick({ ...sub.currentBar });
                  }
                  const seedOpen = sub.lastClose != null ? sub.lastClose : price;
                  sub.currentBar = {
                    time: barTimeSec,
                    open: seedOpen,
                    high: Math.max(seedOpen, price),
                    low: Math.min(seedOpen, price),
                    close: price,
                    volume,
                  };
                  sub.lastPushMs = Date.now();
                  sub.onTick({ ...sub.currentBar });
                  return;
                }
                sub.currentBar.high = Math.max(sub.currentBar.high, price);
                sub.currentBar.low = Math.min(sub.currentBar.low, price);
                sub.currentBar.close = price;
                sub.currentBar.volume = (sub.currentBar.volume || 0) + volume;

                const nowMs = Date.now();
                // Heartbeat: mark this sub "live" every time a tick lands.
                // If no tick lands for 10s (HEARTBEAT_TIMEOUT_MS below), a
                // watchdog flips `window.__tradelog_feed_stale[ticker]` to
                // true so the UI can show a "feed stalled" pill. Pattern
                // borrowed from user's multiSymbolDatafeed-v3.ts spec.
                sub.lastTickMs = nowMs;
                if (typeof window !== "undefined") {
                  window.__tradelog_feed_stale = window.__tradelog_feed_stale || {};
                  if (window.__tradelog_feed_stale[sub.ticker]) {
                    window.__tradelog_feed_stale[sub.ticker] = false;
                  }
                }
                if (nowMs - sub.lastPushMs < MIN_PUSH_MS) return;
                sub.lastPushMs = nowMs;
                sub.onTick({ ...sub.currentBar });
              } catch (_) {
                /* malformed frame — ignore */
              }
            };

            ws.onclose = () => {
              // Reconnect with a short backoff unless the subscription was
              // explicitly torn down.
              if (!sub.closed) {
                setTimeout(connect, 1500);
              }
            };
            ws.onerror = () => {
              try { ws.close(); } catch (_) { /* ignore */ }
            };
          };
          connect();

          // Watchdog: check every 5s that a tick has landed in the last
          // 10s. If not, flag the feed as stale so the UI (e.g. a pill on
          // the chart header) can surface it. When ticks resume the
          // onmessage handler above flips the flag back to `false`.
          const HEARTBEAT_TIMEOUT_MS = 10_000;
          sub.heartbeat = setInterval(() => {
            if (sub.closed) return;
            const gap = Date.now() - (sub.lastTickMs || 0);
            if (gap > HEARTBEAT_TIMEOUT_MS && typeof window !== "undefined") {
              window.__tradelog_feed_stale = window.__tradelog_feed_stale || {};
              window.__tradelog_feed_stale[sub.ticker] = true;
            }
          }, 5000);
        };
      }

      if (prop === "unsubscribeBars") {
        return function unsubscribeBars(listenerGuid) {
          const sub = subscribers[listenerGuid];
          if (sub) {
            sub.closed = true;
            if (sub.heartbeat) { try { clearInterval(sub.heartbeat); } catch (_) {} }
            try { sub.ws && sub.ws.close(); } catch (_) { /* ignore */ }
            delete subscribers[listenerGuid];
            return;
          }
          return target.unsubscribeBars(listenerGuid);
        };
      }

      // Everything else — history, config, symbols, quotes, search —
      // delegates unchanged.
      const v = target[prop];
      return typeof v === "function" ? v.bind(target) : v;
    },
  });
}
